import { Money } from '../ui/Money';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Wallet, Download, FileSpreadsheet, FileText, Loader2, CheckCircle2, Clock, AlertTriangle, Mail, Send, Plus, X, Landmark } from 'lucide-react';
import { DirhamIcon } from '../icons/DirhamIcon';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { SendReportButton } from '../common/SendReportButton';
import { downloadBlob } from '../../utils/downloadBlob';
import { History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const aedText = (v) => `AED ${Math.round(Number(v || 0)).toLocaleString('en-US')}`;
const aed = (v) => <Money value={v} />;
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
};

const STATUS_META = {
  pending: { label: 'Pending', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  vault: { label: 'In Vault', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

// Per-employee installment payment history (dates + amounts + notes).
const PaymentHistory = ({ row }) => {
  const installments = (row.installments || []).slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const paid = installments.reduce((s, i) => s + Number(i.amount || 0), 0);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-gray-500 hover:text-teal-700"
          data-testid={`eosb-history-${row.employee_code}`}
        >
          <History className="h-3.5 w-3.5 mr-1" />
          {installments.length || 0}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end" data-testid={`eosb-history-popover-${row.employee_code}`}>
        <div className="px-3 py-2 border-b bg-gray-50">
          <p className="text-sm font-semibold text-gray-800">{row.name}</p>
          <p className="text-[11px] text-gray-500">Payment history · {installments.length} installment(s)</p>
        </div>
        {installments.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-gray-400">No payments recorded yet.</div>
        ) : (
          <div className="max-h-56 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-white text-[10px] uppercase text-gray-400 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5">Date</th>
                  <th className="text-right px-3 py-1.5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {installments.map((i, idx) => (
                  <tr key={i.id || idx} className="border-t" data-testid={`eosb-history-row-${row.employee_code}-${idx}`}>
                    <td className="px-3 py-1.5">
                      <span className="text-gray-700">{fmtDate(i.date)}</span>
                      {i.notes ? <span className="block text-[10px] text-gray-400">{i.notes}</span> : null}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{aed(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-gray-50 font-semibold">
                  <td className="px-3 py-1.5 text-gray-600">Total paid</td>
                  <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{aed(paid)}</td>
                </tr>
                <tr className="border-t">
                  <td className="px-3 py-1.5 text-gray-600">Balance</td>
                  <td className="px-3 py-1.5 text-right font-mono text-rose-700">{aed(row.balance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const EOSBCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [partial, setPartial] = useState(null); // { row } when recording a partial payment
  const [partialAmt, setPartialAmt] = useState('');
  const [showPaid, setShowPaid] = useState(false); // unpaid-only by default
  const [vaultOnly, setVaultOnly] = useState(false); // tile-driven filter to vault rows
  const [financeOpen, setFinanceOpen] = useState(false);
  const [financeEmails, setFinanceEmails] = useState([]);
  const [newFinanceEmail, setNewFinanceEmail] = useState('');
  const [sendingFinance, setSendingFinance] = useState(false);

  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const openFinance = async () => {
    setFinanceOpen(true);
    try {
      const r = await axios.get(`${API}/api/settings/eosb-finance`);
      setFinanceEmails(r.data?.current?.finance_emails || []);
    } catch { /* leave empty */ }
  };
  const addFinanceEmail = () => {
    const e = newFinanceEmail.trim();
    if (!EMAIL_RE.test(e)) { toast({ title: 'Enter a valid email', variant: 'destructive' }); return; }
    if (financeEmails.some(x => x.toLowerCase() === e.toLowerCase())) { setNewFinanceEmail(''); return; }
    setFinanceEmails(prev => [...prev, e]);
    setNewFinanceEmail('');
  };
  const removeFinanceEmail = (e) => setFinanceEmails(prev => prev.filter(x => x !== e));
  const sendFinance = async () => {
    if (financeEmails.length === 0) { toast({ title: 'Add at least one email', variant: 'destructive' }); return; }
    setSendingFinance(true);
    try {
      const r = await axios.post(`${API}/api/settings/eosb-finance/send-now`, { recipients: financeEmails, save: true });
      const t = r.data?.totals || {};
      toast({ title: 'EOSB summary emailed to Finance', description: `Sent to ${(r.data?.to || []).join(', ')} (cc ${r.data?.cc}) · ${t.count || 0} pending, balance ${aedText(t.balance)}` });
      setFinanceOpen(false);
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSendingFinance(false);
    }
  };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/eosb-payments/summary/dashboard`);
      setData(r.data);
    } catch {
      setData({ floats: {}, counts: {}, rows: [] });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (row, status, amount = null) => {
    setBusyId(row.employee_id);
    try {
      await axios.put(`${API}/api/eosb-payments/${row.employee_id}/set-status`, { status, amount });
      toast({ title: 'EOSB updated', description: `${row.name} → ${STATUS_META[status]?.label || status}` });
      await load();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const onStatusChange = (row, next) => {
    if (next === 'partial') {
      setPartialAmt('');
      setPartial(row);
    } else {
      setStatus(row, next);
    }
  };

  const releaseAndPay = async (row) => {
    const amt = row.vault_amount || row.total_amount;
    if (!window.confirm(
      `Release ${row.name}'s EOSB from the Company Vault and record it as PAID OUT `
      + `(AED ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})?`
    )) return;
    setBusyId(row.employee_id);
    try {
      await axios.post(`${API}/api/staff-vault/release`, {
        employee_id: row.employee_id,
        released_to: 'employee',
        notes: 'Released from vault & paid out (one-click)',
      });
      await axios.put(`${API}/api/eosb-payments/${row.employee_id}/mark-paid?total_amount=${row.total_amount}`);
      toast({ title: 'Released & paid out', description: `${row.name}'s EOSB settled and removed from the vault.` });
      await load();
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const submitPartial = () => {
    const amt = parseFloat(partialAmt);
    if (!amt || amt <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    const row = partial;
    setPartial(null);
    setStatus(row, 'partial', amt);
  };

  const downloadPending = async (format) => {
    try {
      const r = await axios.get(`${API}/api/eosb-payments/report/download?status=pending&format=${format}`, { responseType: 'blob' });
      const type = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const blob = new Blob([r.data], { type });
      downloadBlob(blob, `EOSB_Pending.${format === 'excel' ? 'xlsx' : 'pdf'}`, type);
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  };

  const floats = data?.floats || {};
  const counts = data?.counts || {};
  const rows = data?.rows || [];
  // Tile filter takes precedence: show only vault rows when toggled on.
  // Otherwise: Unpaid (pending + partial) on top; Paid hidden unless toggled on.
  const visibleRows = vaultOnly
    ? rows.filter((r) => r.status === 'vault')
    : (showPaid ? rows : rows.filter((r) => r.status !== 'paid'));

  return (
    <Card data-testid="eosb-dashboard-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-5 w-5 text-teal-600" />
            End of Service Benefits (EOSB)
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer mr-1" data-testid="eosb-show-paid-toggle-label">
              <Switch checked={showPaid} onCheckedChange={setShowPaid} data-testid="eosb-show-paid-toggle" />
              Show paid
            </label>
            <SendReportButton
              reportUrl="/api/eosb-payments/report/download?status=pending&format=pdf"
              filename="EOSB_Pending.pdf"
              defaultSubject="Pending EOSB — Quick Review"
              size="sm"
              className="h-8 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              testid="eosb-email"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={openFinance}
              data-testid="eosb-email-finance-btn"
            >
              <Mail className="h-4 w-4 mr-1" /> Email Finance
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8" data-testid="eosb-download-btn">
                  <Download className="h-4 w-4 mr-1" /> Pending
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => downloadPending('pdf')} data-testid="eosb-download-pdf">
                  <FileText className="h-4 w-4 mr-2" /> Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadPending('excel')} data-testid="eosb-download-excel">
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Floats on top */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 ${(counts.vault || 0) > 0 ? 'lg:grid-cols-4' : ''} gap-3 mb-4`}>
          <div className="rounded-xl border bg-gradient-to-br from-teal-50 to-white p-4" data-testid="eosb-float-due">
            <div className="flex items-center gap-2 text-teal-700"><DirhamIcon className="h-4 w-4" /><span className="text-xs font-medium">Total EOSB Due</span></div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{aed(floats.total_due)}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{data?.total_records || 0} record(s)</p>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="eosb-float-paid">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /><span className="text-xs font-medium">Paid (down)</span></div>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{aed(floats.total_paid)}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{counts.paid || 0} settled</p>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-rose-50 to-white p-4" data-testid="eosb-float-unpaid">
            <div className="flex items-center gap-2 text-rose-700"><Clock className="h-4 w-4" /><span className="text-xs font-medium">Unpaid (balance)</span></div>
            <p className="text-2xl font-bold text-rose-700 mt-1">{aed(floats.total_unpaid)}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{(counts.pending || 0) + (counts.partial || 0)} outstanding</p>
          </div>
          {(counts.vault || 0) > 0 && (
            <button
              type="button"
              onClick={() => setVaultOnly((v) => !v)}
              className={`text-left rounded-xl border bg-gradient-to-br from-indigo-50 to-white p-4 transition-all hover:shadow-md hover:border-indigo-300 ${vaultOnly ? 'ring-2 ring-indigo-400 border-indigo-300' : ''}`}
              data-testid="eosb-float-vault"
              title={vaultOnly ? 'Click to clear filter' : 'Click to show only unclaimed (in-vault) EOSB'}
            >
              <div className="flex items-center gap-2 text-indigo-700"><Landmark className="h-4 w-4" /><span className="text-xs font-medium">Unclaimed EOSB</span></div>
              <p className="text-2xl font-bold text-indigo-700 mt-1">{aed(floats.total_vault)}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{counts.vault} in Company Vault{vaultOnly ? ' · filtering' : ''}</p>
            </button>
          )}
        </div>

        {/* Breakdown table with status control */}
        <div className="border rounded-lg overflow-x-auto">
          {loading ? (
            <div className="py-8 text-center text-gray-400 text-sm">Loading EOSB…</div>
          ) : visibleRows.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm" data-testid="eosb-empty">
              {rows.length === 0 ? 'No EOSB records yet.' : 'No outstanding EOSB — all settled. Toggle “Show paid” to view settled records.'}
            </div>
          ) : (
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="text-left p-2.5">Employee</th>
                  <th className="text-right p-2.5">Total</th>
                  <th className="text-right p-2.5">Paid</th>
                  <th className="text-right p-2.5">Balance</th>
                  <th className="text-center p-2.5">History</th>
                  <th className="text-left p-2.5 w-40">Status / Control</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.employee_id} className="border-t hover:bg-gray-50/50" data-testid={`eosb-row-${r.employee_code}`}>
                    <td className="p-2.5">
                      <span className="font-medium text-gray-900">{r.name}</span>
                      <span className="text-xs text-gray-400 ml-1.5 font-mono">{r.employee_code}</span>
                      <span className="block text-[11px] text-gray-400">{r.employment_status}{r.department ? ` · ${r.department}` : ''}</span>
                      {r.under_one_year && (
                        <Badge
                          variant="outline"
                          className="mt-1 bg-amber-50 text-amber-700 border-amber-300 text-[10px] font-medium gap-1"
                          title={`Service ${r.service_years} years — under 12 months. No gratuity is due under UAE law.`}
                          data-testid={`eosb-not-eligible-${r.employee_code}`}
                        >
                          <AlertTriangle className="h-3 w-3" /> Not eligible — under 1 year
                        </Badge>
                      )}
                    </td>
                    <td className="p-2.5 text-right font-mono">{aed(r.total_amount)}</td>
                    <td className="p-2.5 text-right font-mono text-emerald-700">{aed(r.paid_amount)}</td>
                    <td className="p-2.5 text-right font-mono text-rose-700 font-semibold">{aed(r.balance)}</td>
                    <td className="p-2.5 text-center">
                      <PaymentHistory row={r} />
                    </td>
                    <td className="p-2.5">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label}</Badge>
                        {r.status === 'vault' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-1"
                            disabled={busyId === r.employee_id}
                            onClick={() => releaseAndPay(r)}
                            data-testid={`eosb-release-pay-${r.employee_code}`}
                          >
                            <Landmark className="h-3 w-3" />
                            {busyId === r.employee_id ? 'Saving…' : 'Release & Pay Out'}
                          </Button>
                        ) : (
                          <Select
                            value=""
                            onValueChange={(v) => onStatusChange(r, v)}
                            disabled={busyId === r.employee_id || r.total_amount <= 0}
                          >
                            <SelectTrigger className="h-7 w-[120px] text-xs" data-testid={`eosb-status-${r.employee_code}`}>
                              <SelectValue placeholder={busyId === r.employee_id ? 'Saving…' : 'Change…'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="paid">Mark Fully Paid</SelectItem>
                              <SelectItem value="partial">Record Partial…</SelectItem>
                              <SelectItem value="pending">Reset to Unpaid</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>

      {/* Partial payment dialog */}
      <Dialog open={!!partial} onOpenChange={(o) => { if (!o) setPartial(null); }}>
        <DialogContent className="max-w-sm" data-testid="eosb-partial-dialog">
          <DialogHeader>
            <DialogTitle>Record Partial Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{partial?.name} — balance {aed(partial?.balance)}</p>
            <Label className="text-xs">Payment amount (AED)</Label>
            <Input
              type="number"
              value={partialAmt}
              onChange={(e) => setPartialAmt(e.target.value)}
              placeholder="e.g. 5000"
              autoFocus
              data-testid="eosb-partial-amount"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPartial(null)}>Cancel</Button>
            <Button onClick={submitPartial} className="bg-teal-600 hover:bg-teal-700" data-testid="eosb-partial-submit">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Email Finance — quick-send the pending EOSB summary */}
      <Dialog open={financeOpen} onOpenChange={setFinanceOpen}>
        <DialogContent className="max-w-md" data-testid="eosb-finance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-teal-600" /> Email Pending EOSB to Finance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Sends the pending-EOSB summary (PDF + outstanding totals) to the recipients below.
              Auto-CC'd to <b>hr@starsnbars.ae</b>. Saved recipients are reused for the monthly auto-email.
            </p>
            <div>
              <Label className="text-xs">Recipient email(s)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="email"
                  value={newFinanceEmail}
                  onChange={(e) => setNewFinanceEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFinanceEmail(); } }}
                  placeholder="finance@company.com"
                  data-testid="eosb-finance-email-input"
                />
                <Button variant="outline" onClick={addFinanceEmail} className="gap-1" data-testid="eosb-finance-email-add">
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            </div>
            {financeEmails.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No recipients yet — add at least one Finance email.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="eosb-finance-email-list">
                {financeEmails.map((em) => (
                  <Badge key={em} variant="outline" className="gap-1 pr-1 text-sm font-normal" data-testid={`eosb-finance-chip-${em}`}>
                    {em}
                    <button onClick={() => removeFinanceEmail(em)} className="ml-1 rounded hover:bg-gray-200 p-0.5" data-testid={`eosb-finance-remove-${em}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFinanceOpen(false)}>Cancel</Button>
            <Button
              onClick={sendFinance}
              disabled={sendingFinance || financeEmails.length === 0}
              className="bg-teal-600 hover:bg-teal-700 gap-1.5"
              data-testid="eosb-finance-send-btn"
            >
              {sendingFinance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Summary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EOSBCard;
